import { useState } from "react";
import { useGetVaAudiologistMetricsQuery } from "../../app/vaApi/vaApi";
import { extractAudiologistMetricsFromResult } from "../../helpers/vaMetricsUtils";
import MetricsPeriodFilter from "../distributor/MetricsPeriodFilter";
import Button from "../shared/buttons/button";
import { shouldSkipAuthedQuery } from "../../helpers/authSession";

const METRICS_CONFIG = [
  { key: "totalOrders", label: "Total Orders", isCount: true },
  { key: "totalSpend", label: "Total Spend" },
  { key: "averageOrderValue", label: "Average Order Value" },
  { key: "totalReturns", label: "Total Returns", isCount: true, negative: true },
  { key: "returnsAmount", label: "Returns Amount", negative: true },
];

const EMPTY_METRICS = {
  totalOrders: 0,
  totalSpend: 0,
  averageOrderValue: 0,
  totalReturns: 0,
  returnsAmount: 0,
};

function formatCurrency(value) {
  const num = Number(value) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatCount(value) {
  return new Intl.NumberFormat("en-US").format(Number(value) || 0);
}

function MetricsSkeleton() {
  return (
    <div className="distributor-metrics-bar__metrics distributor-metrics-bar__metrics--loading">
      {METRICS_CONFIG.map((metric) => (
        <div
          key={metric.key}
          className="distributor-metric distributor-metrics-card distributor-metric--skeleton"
        >
          <div className="distributor-metric__skeleton-label" />
          <div className="distributor-metric__skeleton-value" />
        </div>
      ))}
    </div>
  );
}

function MetricsValues({ metrics }) {
  return (
    <div className="distributor-metrics-bar__metrics">
      {METRICS_CONFIG.map((metric) => {
        const value = metrics[metric.key];
        const display = metric.isCount
          ? formatCount(value)
          : formatCurrency(value);

        return (
          <div
            key={metric.key}
            className="distributor-metric distributor-metrics-card"
          >
            <span className="distributor-metric__label">{metric.label}</span>
            <span
              className={`distributor-metric__value ${
                metric.negative ? "distributor-metric__value--negative" : ""
              }`}
            >
              {display}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function AudiologistMetricsBar() {
  const [filter, setFilter] = useState({ period: "this_month" });
  const skip = shouldSkipAuthedQuery();
  const { data, isLoading, isError, refetch } =
    useGetVaAudiologistMetricsQuery(filter, { skip });

  const metrics = extractAudiologistMetricsFromResult(data) ?? EMPTY_METRICS;
  const showError = isError && !skip;

  return (
    <div className="distributor-metrics-bar">
      <div className="distributor-metrics-bar__filter distributor-metrics-card">
        <MetricsPeriodFilter value={filter} onChange={setFilter} />
      </div>

      {showError ? (
        <div className="distributor-metrics-bar__error distributor-metrics-card">
          <span className="text-danger">Failed to load metrics.</span>
          <Button label="Retry" className="btn-grey" onClick={refetch} />
        </div>
      ) : isLoading || skip ? (
        <MetricsSkeleton />
      ) : (
        <MetricsValues metrics={metrics} />
      )}
    </div>
  );
}
